<script lang="ts">
import type { ThemeSlots } from '../types';
import { tv } from 'tailwind-variants';

const colorSwatches = tv({
	slots: {
		root: 'flex flex-wrap items-center gap-2',
		swatch: `
			flex size-6 shrink-0 items-center justify-center rounded-full border border-(--border-light) transition-all
			hover:scale-110 hover:ring-2
			focus:outline-none
			focus-visible:scale-110 focus-visible:ring-1 focus-visible:ring-primary
			dark:border-(--border-dark)
		`,
	},
});

export interface ColorSwatchesProps {
	presets?: string[];
	ui?: ThemeSlots<typeof colorSwatches>;
}
</script>

<script lang="ts" setup>
import type { AcceptableValue } from 'reka-ui';
import { RadioGroupItem, RadioGroupRoot } from 'reka-ui';
import { computed } from 'vue';

import { useAppConfig } from '#app/config';
import { DEFAULT_COLORS, getColorInfo } from '#layers/ui/utils/colors';

const props = withDefaults(defineProps<ColorSwatchesProps>(), {
	presets: () => [...DEFAULT_COLORS],
});

const modelValue = defineModel<string | null>();

const appConfig = useAppConfig();
const theme = computed(() => colorSwatches());

function borderStyle(color?: string | null) {
	const colorInfo = color ? getColorInfo(color) : undefined;
	return {
		'--border-light': color && colorInfo?.isLight ? `color-mix(in srgb, ${color} 90%, black)` : 'transparent',
		'--border-dark': color && colorInfo?.isDark ? `color-mix(in srgb, ${color} 70%, white)` : 'transparent',
	};
}

function handleSelect(color: AcceptableValue) {
	if (typeof color !== 'string') return;

	modelValue.value = color;
}
</script>

<template>
	<RadioGroupRoot :class="theme.root({ class: [props.ui?.root] })" :model-value @update:model-value="handleSelect">
		<RadioGroupItem
			v-for="preset in props.presets"
			v-slot="{ checked }"
			:key="preset"
			:value="preset"
			:class="theme.swatch({ class: [props.ui?.swatch, getColorInfo(preset).isLight ? 'text-dark' : 'text-white'] })"
			:style="{
				'backgroundColor': preset,
				'--tw-ring-color': `color-mix(in srgb, ${preset} 30%, transparent)`,
				...borderStyle(preset),
			}"
		>
			<DIcon v-if="checked" :name="appConfig.ui.icons.check" class="size-3" />
		</RadioGroupItem>
		<slot name="trailing" />
	</RadioGroupRoot>
</template>
